import React, { useCallback, useRef, useState } from 'react';

import { Box, CircularProgress, IconButton, Link, Typography } from '@mui/material';
import { DeleteIcon, UploadDocumentIcon, UploadedFileIcon } from '@sharedCR/coreDesignSystem/icons';
import { captureDocumentPhoto } from 'app/services/nativeCamera';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { getIsNativePlatform } from 'utils/platform';

import {
  dragDropStyles,
  getDragAndDropStyles,
  infoMessageStyles,
  loaderStyles,
  previewImageStyles,
  previewStyles,
  uploadedIconStyles,
} from './FileUploadCDS.styles';

const FileUploadCDS = ({
  selectedFile = null,
  setSelectedFile,
  errorMessage = null,
  setErrorMessage,
  acceptedFileTypes,
  callToActionMessage = 'Please choose a file to upload',
  isLoading = false,
  disabled = false,
  isRemoveButtonVisible = true,
  previewUrl = null,
  infoMessage = null,
}) => {
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isWrongFileType, setIsWrongFileType] = useState(false);

  const isCameraCaptureAvailable =
    getIsNativePlatform() && acceptedFileTypes.some(type => type.startsWith('image/'));

  const handleFileSelect = useCallback(
    file => {
      setErrorMessage(null);
      setIsDragActive(false);
      setIsWrongFileType(false);
      setSelectedFile(file);
    },
    [setSelectedFile, setErrorMessage],
  );

  const onTakePhoto = useCallback(
    async event => {
      event?.stopPropagation();
      const file = await captureDocumentPhoto();

      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleBrowseClick = useCallback(() => {
    if (disabled || isLoading) {
      return;
    }

    inputRef.current?.click();
  }, [disabled, isLoading]);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];

      if (file && !acceptedFileTypes.includes(file.type)) {
        setIsWrongFileType(true);

        return;
      }

      handleFileSelect(file);
    },
    [handleFileSelect, acceptedFileTypes],
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsWrongFileType(false);
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    setIsDragActive(false);
    setIsWrongFileType(false);
  }, []);

  const handleFileInputChange = useCallback(
    e => {
      e.preventDefault();
      const file = e.target.files[0];
      handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleRemoveFile = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    setSelectedFile(null);
    setErrorMessage(null);
    setIsDragActive(false);
    setIsWrongFileType(false);
  }, [setSelectedFile, setErrorMessage]);

  return (
    <div className="relative">
      {isLoading && (
        <Box sx={loaderStyles}>
          <CircularProgress size={50} />
        </Box>
      )}
      <div className={clsx('flex flex-col gap-4 ', isLoading && 'opacity-50')}>
        {selectedFile && (
          <div className="flex items-center gap-8 mb-20 relative">
            <Box sx={uploadedIconStyles}>
              <UploadedFileIcon />
            </Box>
            <Typography className="italic underline" color="text.link" variant="regular16">
              {selectedFile.name}
            </Typography>
            {isRemoveButtonVisible && (
              <IconButton aria-label="Remove file" className="ml-24" onClick={handleRemoveFile}>
                <DeleteIcon />
              </IconButton>
            )}
            {errorMessage && (
              <Typography className="absolute top-[100%]" color="text.error" variant="regular12">
                {errorMessage}
              </Typography>
            )}
          </div>
        )}
        <Typography className="mb-16" variant="bold16">
          {callToActionMessage}
        </Typography>
        <Box
          sx={getDragAndDropStyles({
            active: isDragActive,
            disabled,
            error: isWrongFileType,
          })}
          onClick={handleBrowseClick}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadDocumentIcon disabled={disabled} error={isWrongFileType} size={48} />
          <Typography sx={dragDropStyles} variant="medium14">
            Drag & Drop or{' '}
            <input
              accept={acceptedFileTypes.join(',')}
              ref={inputRef}
              type="file"
              onChange={handleFileInputChange}
            />
            <Link className="cursor-pointer" data-testid="choose-file">
              Choose File
            </Link>{' '}
            to Upload
          </Typography>
          {isCameraCaptureAvailable && (
            <Typography className="mt-8" variant="medium14">
              or{' '}
              <Link className="cursor-pointer" data-testid="take-photo" onClick={onTakePhoto}>
                Take a Photo
              </Link>
            </Typography>
          )}
        </Box>
        {previewUrl && (
          <Box sx={previewStyles}>
            <Typography color="text.secondary" variant="regular12">
              Preview:
            </Typography>
            <Box alt="Preview" component="img" src={previewUrl} sx={previewImageStyles} />
          </Box>
        )}
        {!!infoMessage && (
          <Typography color="text.secondary" sx={infoMessageStyles} variant="regular12">
            {infoMessage}
          </Typography>
        )}
      </div>
    </div>
  );
};

FileUploadCDS.propTypes = {
  setSelectedFile: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedFile: PropTypes.object,
  errorMessage: PropTypes.string,
  callToActionMessage: PropTypes.node,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  isRemoveButtonVisible: PropTypes.bool,
  previewUrl: PropTypes.string,
  infoMessage: PropTypes.string,
};

export default FileUploadCDS;
